import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Truck, Wrench, ClipboardCheck, 
  ChevronDown, ChevronRight,
  Settings, LogOut, Palette, Volume2, VolumeX,
  Shield, Users, FileCheck, ClipboardList,
  Zap, MapPin, Clock, AlertTriangle,
  Plus, X, ExternalLink
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// App configurations with URLs for SSO
const APPS = [
  { id: 'ridero', name: 'Ridero', icon: Truck, color: '#22c55e', url: 'https://www.rideroexpress.com/login' },
  { id: 'mechanic_kiosk', name: 'Kiosk', icon: Wrench, color: '#3b82f6', url: 'https://peoriakiosk.com/login' },
  { id: 'pti_pector', name: 'PTI', icon: ClipboardCheck, color: '#f97316', url: 'https://ptipector.com/dashboard' },
];

// Mock data for info cards
const MOCK_CARDS = {
  safety: {
    title: 'Safety Waiting List',
    items: [
      { id: 1, text: 'Unit 404 - Brake Inspection', status: 'urgent', time: '15m' },
      { id: 2, text: 'Unit 218 - Tire Check', status: 'pending', time: '1h' },
      { id: 3, text: 'Unit 512 - Light Repair', status: 'done', time: '2h' },
    ]
  },
  mechanic: {
    title: 'Shop Bay Status',
    bays: [
      { id: 1, status: 'busy', truck: 'Unit 301', task: 'Oil Change' },
      { id: 2, status: 'free', truck: null, task: null },
      { id: 3, status: 'busy', truck: 'Unit 118', task: 'Transmission' },
      { id: 4, status: 'free', truck: null, task: null },
    ]
  },
  drivers: {
    title: 'Drivers Available',
    count: 12,
    onDuty: 8,
    offDuty: 4,
    list: [
      { name: 'John D.', status: 'driving', eta: '45m' },
      { name: 'Mike S.', status: 'available', eta: null },
      { name: 'Sarah K.', status: 'break', eta: '15m' },
    ]
  },
  tasks: {
    title: 'My Tasks',
    items: [
      { id: 1, text: 'Complete PTI for Unit 404', priority: 'high' },
      { id: 2, text: 'Deliver to Chicago Hub', priority: 'medium' },
      { id: 3, text: 'Fuel stop at Station 7', priority: 'low' },
    ]
  },
  trucks: {
    title: 'My 10 Trucks',
    items: [
      { unit: '404', status: 'moving', location: 'I-90 Chicago', fuel: 75 },
      { unit: '218', status: 'parked', location: 'Hub A', fuel: 45 },
      { unit: '512', status: 'maintenance', location: 'Shop', fuel: 90 },
    ]
  },
  compliance: {
    title: 'PTI Compliance Today',
    completed: 8,
    pending: 2,
    failed: 1,
  }
};

export default function HubMainboard() {
  const { user, logout } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const navigate = useNavigate();
  
  // State
  const [isOnline, setIsOnline] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastTap, setLastTap] = useState(0);
  const [expandedSections, setExpandedSections] = useState(['safety', 'tasks']);
  
  // Double tap handler for FAB
  const handleFabClick = useCallback(() => {
    const now = Date.now();
    if (now - lastTap < 300) {
      // Double tap detected
      setShowQuickMenu(true);
    }
    setLastTap(now);
  }, [lastTap]);

  // Toggle online status
  const toggleOnlineStatus = async () => {
    setIsOnline(!isOnline);
    
    // Send status to backend (silent)
    try {
      await axios.post(`${API}/users/status`, { 
        online: !isOnline 
      }, { withCredentials: true });
    } catch (e) {
      // Status update optional
    }
  };

  // Handle app selection
  // Handle app selection - view data or launch SSO
  const handleAppSelect = (appId) => {
    if (selectedApp === appId) {
      setSelectedApp(null);
    } else {
      setSelectedApp(appId);
      toast.info(`Viewing ${APPS.find(a => a.id === appId)?.name} data`);
    }
  };

  // Launch app with SSO
  const handleAppLaunch = async (appId) => {
    const app = APPS.find(a => a.id === appId);
    if (!app?.url) {
      toast.error('App URL not configured');
      return;
    }

    try {
      toast.loading(`Connecting to ${app.name}...`);
      
      // Always get fresh SSO token from API using HttpOnly cookie auth
      const res = await axios.get(`${API}/sso/token?app_id=${appId}`, { withCredentials: true });
      const ssoToken = res.data.token;
      const state = res.data.state || '';  // CSRF protection
      
      if (!ssoToken) {
        toast.dismiss();
        toast.error('Failed to get SSO token');
        return;
      }

      // Build redirect URL with state for CSRF protection
      const redirectUrl = `${app.url}?session_token=${ssoToken}&state=${state}`;
      
      toast.dismiss();
      toast.success(`Opening ${app.name}...`);
      
      // Open in new tab
      window.open(redirectUrl, '_blank');
      
    } catch (error) {
      toast.dismiss();
      toast.error(`Failed to connect: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isMasterAdmin = user?.role_id === 'master_admin' || user?.role_id === 'admin';

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background noise texture */}
      <div className="fixed inset-0 bg-noise opacity-5 pointer-events-none" />
      
      {/* === FIXED LOGO AT TOP CENTER === */}
      <div className="fixed top-0 left-0 right-0 z-[100] flex justify-center pt-2 pointer-events-none">
        <button
          onClick={toggleOnlineStatus}
          className="pointer-events-auto transition-transform active:scale-95"
          data-testid="online-toggle-btn"
        >
          <img 
            src="/logo-full.png"
            alt="Leader Hub" 
            className={`h-28 w-28 object-contain transition-all duration-700 ${
              isOnline 
                ? 'drop-shadow-[0_0_30px_rgba(218,165,32,0.9)] brightness-110 saturate-110' 
                : 'drop-shadow-[0_0_5px_rgba(50,50,50,0.3)] brightness-[0.3] saturate-50'
            }`}
          />
        </button>
      </div>
      
      {/* === HEADER === */}
      <header className="sticky top-0 z-50 w-full bg-card/80 backdrop-blur-xl border-b border-border/30 pt-28">
        <div className="flex flex-col items-center pb-2 px-4 gap-2">

          {/* User info */}
          <div className="text-center">
            <p className="text-lg font-bold font-secondary uppercase tracking-tight text-foreground">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-muted-foreground">
              {user?.points_balance || 0} pts • {user?.role_id?.replace('_', ' ')}
            </p>
          </div>

          {/* App Selector Row */}
          <div className="flex gap-3 w-full justify-center py-2">
            {APPS.map((app) => {
              const Icon = app.icon;
              const isSelected = selectedApp === app.id;
              return (
                <button
                  key={app.id}
                  onClick={() => handleAppSelect(app.id)}
                  className={`flex-shrink-0 w-16 h-16 rounded-xl bg-card border flex flex-col items-center justify-center gap-1 transition-all active:scale-90 ${
                    isSelected 
                      ? 'border-accent shadow-[0_0_15px_rgba(249,115,22,0.4)]' 
                      : 'border-border/50 hover:border-border'
                  }`}
                  style={isSelected ? { borderColor: app.color } : {}}
                  data-testid={`app-select-${app.id}`}
                >
                  <Icon 
                    className="w-6 h-6" 
                    style={{ color: isSelected ? app.color : 'currentColor' }}
                  />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    {app.name}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Selected App Indicator with Launch Button */}
          {selectedApp && (
            <div 
              className="w-full py-3 px-4 rounded-lg flex items-center justify-between animate-fade-in-up"
              style={{ 
                backgroundColor: `${APPS.find(a => a.id === selectedApp)?.color}20`,
              }}
            >
              <span 
                className="text-sm font-bold uppercase tracking-wider"
                style={{ color: APPS.find(a => a.id === selectedApp)?.color }}
              >
                {APPS.find(a => a.id === selectedApp)?.name}
              </span>
              <button
                onClick={() => handleAppLaunch(selectedApp)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase bg-white/10 hover:bg-white/20 transition-colors"
                style={{ color: APPS.find(a => a.id === selectedApp)?.color }}
              >
                <ExternalLink className="w-4 h-4" />
                Open App
              </button>
            </div>
          )}
        </div>
      </header>

      {/* === MAINBOARD CONTENT === */}
      <main className="px-2 pb-24 pt-4">
        <Accordion 
          type="multiple" 
          value={expandedSections}
          onValueChange={setExpandedSections}
          className="space-y-2"
        >
          
          {/* Safety Section */}
          <AccordionItem value="safety" className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
            <AccordionTrigger className="flex items-center justify-between w-full p-4 text-lg font-secondary uppercase tracking-wider hover:bg-white/5 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-red-500" />
                <span>Safety</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-3 bg-black/20">
              <div className="space-y-2">
                {MOCK_CARDS.safety.items.map((item) => (
                  <div 
                    key={item.id}
                    className={`p-3 rounded-md bg-card border flex items-center justify-between ${
                      item.status === 'urgent' ? 'border-l-4 border-l-red-500' : 'border-border/30'
                    }`}
                  >
                    <div>
                      <p className="font-medium">{item.text}</p>
                      <p className="text-xs text-muted-foreground">ETA: {item.time}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full uppercase font-bold ${
                      item.status === 'urgent' ? 'bg-red-500/20 text-red-500' :
                      item.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-green-500/20 text-green-500'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Tasks Section */}
          <AccordionItem value="tasks" className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
            <AccordionTrigger className="flex items-center justify-between w-full p-4 text-lg font-secondary uppercase tracking-wider hover:bg-white/5 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-blue-500" />
                <span>My Tasks</span>
                <span className="bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full">
                  {MOCK_CARDS.tasks.items.length}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-3 bg-black/20">
              <div className="space-y-2">
                {MOCK_CARDS.tasks.items.map((task) => (
                  <div 
                    key={task.id}
                    className={`p-3 rounded-md bg-card border border-border/30 flex items-center justify-between ${
                      task.priority === 'high' ? 'border-l-4 border-l-red-500' :
                      task.priority === 'medium' ? 'border-l-4 border-l-yellow-500' :
                      'border-l-4 border-l-green-500'
                    }`}
                  >
                    <p className="font-medium">{task.text}</p>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Drivers Section */}
          <AccordionItem value="drivers" className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
            <AccordionTrigger className="flex items-center justify-between w-full p-4 text-lg font-secondary uppercase tracking-wider hover:bg-white/5 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-green-500" />
                <span>Drivers</span>
                <span className="bg-green-500/20 text-green-500 text-xs px-2 py-0.5 rounded-full">
                  {MOCK_CARDS.drivers.onDuty} online
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-3 bg-black/20">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-card p-3 rounded-md text-center border border-border/30">
                  <p className="text-2xl font-bold text-green-500">{MOCK_CARDS.drivers.onDuty}</p>
                  <p className="text-xs text-muted-foreground">On Duty</p>
                </div>
                <div className="bg-card p-3 rounded-md text-center border border-border/30">
                  <p className="text-2xl font-bold text-yellow-500">{MOCK_CARDS.drivers.offDuty}</p>
                  <p className="text-xs text-muted-foreground">Off Duty</p>
                </div>
                <div className="bg-card p-3 rounded-md text-center border border-border/30">
                  <p className="text-2xl font-bold text-foreground">{MOCK_CARDS.drivers.count}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
              <div className="space-y-2">
                {MOCK_CARDS.drivers.list.map((driver, idx) => (
                  <div key={idx} className="p-3 rounded-md bg-card border border-border/30 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        driver.status === 'available' ? 'bg-green-500' :
                        driver.status === 'driving' ? 'bg-blue-500' :
                        'bg-yellow-500'
                      }`} />
                      <p className="font-medium">{driver.name}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {driver.eta ? `ETA: ${driver.eta}` : 'Available'}
                    </span>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Trucks Section */}
          <AccordionItem value="trucks" className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
            <AccordionTrigger className="flex items-center justify-between w-full p-4 text-lg font-secondary uppercase tracking-wider hover:bg-white/5 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-accent" />
                <span>My Trucks</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-3 bg-black/20">
              <div className="space-y-2">
                {MOCK_CARDS.trucks.items.map((truck, idx) => (
                  <div key={idx} className="p-3 rounded-md bg-card border border-border/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold">Unit {truck.unit}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full uppercase font-bold ${
                        truck.status === 'moving' ? 'bg-green-500/20 text-green-500' :
                        truck.status === 'parked' ? 'bg-blue-500/20 text-blue-500' :
                        'bg-yellow-500/20 text-yellow-500'
                      }`}>
                        {truck.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {truck.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {truck.fuel}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Compliance Section */}
          <AccordionItem value="compliance" className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
            <AccordionTrigger className="flex items-center justify-between w-full p-4 text-lg font-secondary uppercase tracking-wider hover:bg-white/5 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-purple-500" />
                <span>PTI Compliance</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-3 bg-black/20">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-card p-4 rounded-md text-center border border-border/30">
                  <p className="text-3xl font-bold text-green-500">{MOCK_CARDS.compliance.completed}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
                <div className="bg-card p-4 rounded-md text-center border border-border/30">
                  <p className="text-3xl font-bold text-yellow-500">{MOCK_CARDS.compliance.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
                <div className="bg-card p-4 rounded-md text-center border border-border/30">
                  <p className="text-3xl font-bold text-red-500">{MOCK_CARDS.compliance.failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Mechanic Shop Section */}
          <AccordionItem value="mechanic" className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
            <AccordionTrigger className="flex items-center justify-between w-full p-4 text-lg font-secondary uppercase tracking-wider hover:bg-white/5 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center gap-3">
                <Wrench className="w-5 h-5 text-blue-500" />
                <span>Shop Bays</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-3 bg-black/20">
              <div className="grid grid-cols-2 gap-2">
                {MOCK_CARDS.mechanic.bays.map((bay) => (
                  <div 
                    key={bay.id}
                    className={`p-3 rounded-md border text-center ${
                      bay.status === 'busy' 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : 'bg-green-500/10 border-green-500/30'
                    }`}
                  >
                    <p className="font-bold">Bay {bay.id}</p>
                    {bay.status === 'busy' ? (
                      <>
                        <p className="text-sm text-foreground">{bay.truck}</p>
                        <p className="text-xs text-muted-foreground">{bay.task}</p>
                      </>
                    ) : (
                      <p className="text-sm text-green-500">Available</p>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>
      </main>

      {/* === FLOATING ACTION BUTTON === */}
      <button
        onClick={handleFabClick}
        className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-accent text-accent-foreground shadow-lg flex items-center justify-center z-40 active:scale-90 transition-transform"
        data-testid="fab-button"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Quick Menu (on double tap) */}
      {showQuickMenu && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fade-in" onClick={() => setShowQuickMenu(false)}>
          <div 
            className="absolute bottom-24 right-6 w-56 bg-card rounded-xl border border-border shadow-2xl overflow-hidden animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Quick Menu</p>
            </div>
            
            <div className="p-2 space-y-1">
              {/* Settings */}
              <button 
                onClick={() => { navigate('/settings'); setShowQuickMenu(false); }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Settings className="w-5 h-5" />
                <span>Settings</span>
              </button>
              
              {/* Theme */}
              <button 
                onClick={() => { 
                  const themeKeys = Object.keys(themes);
                  const currentIndex = themeKeys.indexOf(theme);
                  const nextIndex = (currentIndex + 1) % themeKeys.length;
                  setTheme(themeKeys[nextIndex]);
                  toast.success(`Theme: ${themes[themeKeys[nextIndex]].name}`);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Palette className="w-5 h-5" />
                <span>Change Theme</span>
              </button>
              
              {/* Sound */}
              <button 
                onClick={() => { setSoundEnabled(!soundEnabled); toast.info(soundEnabled ? 'Sound Off' : 'Sound On'); }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
              >
                {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                <span>{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
              </button>
              
              {/* Admin Options (if admin) */}
              {isMasterAdmin && (
                <button 
                  onClick={() => { navigate('/dashboard'); setShowQuickMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors text-accent"
                >
                  <Shield className="w-5 h-5" />
                  <span>Admin Panel</span>
                </button>
              )}
              
              {/* Logout */}
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
